import React from 'react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ThemeProvider } from 'next-themes';
import { AuthProvider } from './context/AuthContext';
import { ModalProvider } from './context/ModalContext';
import './index.css';
import './i18n';
import App from './App';
import ArtAlign from './components/ArtAlign';

// =====================================================================
// v0.4.9.2.3c12 PWA — main.tsx
//
// Changes from c11:
//   - REMOVED DiagnosticOverlay (was used for diagnostics during the
//     white-screen investigation; no longer needed now that the
//     server-side user_type fix (c1_API) and auth-context kick fix
//     (c9) are deployed).
//   - KEPT GlobalErrorBoundary as a safety net for any future render
//     errors.
//   - KEPT the 5-second watchdog as a safety net for any future
//     app-load hangs.
// =====================================================================

interface GlobalBoundaryProps {
  children: React.ReactNode;
}
interface GlobalBoundaryState {
  error: Error | null;
  componentStack: string | null;
}

class GlobalErrorBoundary extends React.Component<GlobalBoundaryProps, GlobalBoundaryState> {
  state: GlobalBoundaryState = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[GlobalErrorBoundary] render error:', error, info);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const payload = {
          message: String(error?.message || error),
          name: String(error?.name || ''),
          stack: String(error?.stack || ''),
          componentStack: String(info?.componentStack || ''),
          capturedAt: new Date().toISOString(),
        };
        window.localStorage.setItem(
          'c12_lastGlobalRenderError',
          JSON.stringify(payload)
        );
      }
    } catch {
      // ignore
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', minHeight: '100vh',
          padding: '20px', fontFamily: 'system-ui, -apple-system, sans-serif',
          background: '#fff', color: '#111',
        }}>
          <h1 style={{ color: '#b91c1c', fontSize: '20px', marginBottom: '8px' }}>
            App error
          </h1>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
            The app failed to load. Saved to localStorage as
            <code style={{ fontSize: '12px' }}> c12_lastGlobalRenderError</code>.
          </p>
          <pre style={{
            background: '#f3f4f6', border: '1px solid #d1d5db',
            borderRadius: '6px', padding: '12px', fontSize: '11px',
            overflow: 'auto', maxHeight: '50vh',
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>
{String(this.state.error?.message || this.state.error)}
{'\n\n'}
{String(this.state.error?.stack || '').split('\n').slice(0, 8).join('\n')}
          </pre>
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 16px', background: '#2563eb', color: '#fff',
                border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer',
              }}
            >
              Reload
            </button>
            <button
              type="button"
              onClick={() => {
                try {
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('currentUser');
                } catch {}
                window.location.href = '/login';
              }}
              style={{
                padding: '8px 16px', background: '#e5e7eb', color: '#111',
                border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer',
              }}
            >
              Sign out &amp; log in
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <GlobalErrorBoundary>
      <AuthProvider>
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
          <ModalProvider>
            {/* v0.4.9.4.8 — keeps the art background's ring centred on the
                element marked [data-art-center] (the shield). Mounted here,
                outside <App>, so it survives route changes. Renders null. */}
            <ArtAlign />
            <App />
          </ModalProvider>
        </ThemeProvider>
      </AuthProvider>
    </GlobalErrorBoundary>
  </StrictMode>
);

document.addEventListener('contextmenu', (e) => e.preventDefault());

// 5-second app-load watchdog — falls back if React fails to mount.
let watchdogFired = false;
function fireAppLoadWatchdog(reason: string) {
  if (watchdogFired) return;
  watchdogFired = true;
  console.warn('[Watchdog] App load watchdog fired:', reason);
  try {
    const root = document.getElementById('root');
    if (!root || (root.children.length > 0 && root.innerHTML.trim().length > 100)) {
      return;
    }
    const div = document.createElement('div');
    div.id = 'c12-app-watchdog';
    div.style.cssText = `
      position: fixed; inset: 0; z-index: 999999;
      background: #fee; color: #111; padding: 20px;
      font-family: system-ui, -apple-system, sans-serif;
      overflow: auto;
    `;
    div.innerHTML = `
      <h1 style="color: #b91c1c; font-size: 20px; margin: 0 0 12px 0;">
        App load watchdog (5s timeout)
      </h1>
      <p style="font-size: 14px; color: #444; margin: 0 0 12px 0;">
        The app didn't render anything visible within 5 seconds.
        Reason: <strong>${reason}</strong>
      </p>
      <button onclick="window.location.reload()"
        style="margin-top: 16px; padding: 10px 20px; background: #2563eb; color: #fff;
          border: none; border-radius: 6px; font-size: 14px; cursor: pointer;"
      >Reload</button>
    `;
    document.body.appendChild(div);
  } catch (e) {
    console.error('[Watchdog] failed to render fallback:', e);
  }
}

window.addEventListener('DOMContentLoaded', () => {
  setTimeout(() => fireAppLoadWatchdog('timeout_5s'), 5000);
});
